/**
 * Prism 8 uses Stencil's lazy loader. Importing from `@prism-ui/react` calls
 * `defineCustomElements()`, which registers all ~117 components upfront as proxy classes,
 * but each component's *implementation* lives in its own chunk (`prism-switch.entry-*.js`)
 * that is only fetched when that element's `connectedCallback` first fires.
 *
 * That is a problem for anything whose first appearance is also its first connect. The Dark
 * Mode switch is the clearest case: the user menu renders its items only while open, so the
 * chunk fetch starts on the user's first menu click. Because the row holding the switch has
 * a fixed height, the space reserves instantly while the control itself stays blank until
 * the chunk lands — which reads as a broken menu rather than a loading one.
 *
 * Connecting those elements once, off-screen and during idle time, moves the fetch to app
 * startup where nothing is waiting on it. The element is discarded immediately afterwards;
 * only the cached module matters.
 *
 * Measured click-to-painted for the switch without this warm-up: ~21ms on an idle localhost,
 * ~192ms at 120ms RTT / 4x CPU, ~483ms at 300ms RTT / 6x CPU.
 */

/**
 * Components whose first connect happens behind user interaction or a route change, so they
 * cannot self-warm.
 *
 * `prism-menu` is the Admin Settings left-hand nav. Its chunk fetch cannot start until the
 * main bundle parses, the session request resolves, and React renders — a fully serialized
 * chain, which is why the nav paints late on a fresh load of `/admin-settings`. Warming it
 * moves the fetch alongside the session request instead of after it. Measured mount-to-painted
 * for the nav on a server-cached, browser-cold load: 635ms without the warm-up, 0ms with it.
 * Item count is irrelevant (1 item and 16 items both paint in ~60ms once the module is
 * resident), which is what identifies the cost as module acquisition rather than rendering.
 *
 * `prism-datepicker` is on Admin Settings → Application Windows, which renders four pickers
 * per chapter row. Two separate costs were measured in the design lab, and the obvious one
 * turned out to be the smaller:
 *
 *   - Chunk acquisition is minor. Mounting 20 pickers took 1094ms cold and 966ms warm, so the
 *     module fetch accounts for only ~128ms of it.
 *   - Icon acquisition dominates. Each instance requests four SVGs from `assets.comcast.com`
 *     (calendar, caret-left, caret-right, close) and Prism does *not* dedupe them: five
 *     pickers issued 20 requests for 4 unique URLs. A six-chapter Application Windows page
 *     therefore fires ~96 cross-origin requests against a six-connection-per-host limit,
 *     which is what makes the date fields appear long after the rest of the table.
 *
 * Warming one picker fixes both at once: it pulls the chunk and populates the HTTP cache with
 * all four icons, so every later instance mounts against cache instead of the network. Note
 * this depends on `assets.comcast.com` serving cacheable responses; if it ever sends
 * `no-store`, the icon half of this warm-up silently stops helping.
 */
const DEFAULT_TAGS = ["prism-switch", "prism-menu", "prism-datepicker"];

export default function warmPrismComponents(tags = DEFAULT_TAGS) {
  if (typeof document === "undefined" || typeof window === "undefined") {
    return () => {};
  }

  const host = document.createElement("div");
  host.setAttribute("aria-hidden", "true");
  host.setAttribute("data-prism-warmup", "");
  // Off-screen rather than `display: none`, so we do not depend on Stencil's behaviour for
  // undisplayed elements. `inert` keeps the throwaway controls out of the tab order.
  host.setAttribute("inert", "");
  host.style.cssText =
    "position:absolute;left:-9999px;top:0;width:1px;height:1px;overflow:hidden;pointer-events:none;";

  let observer = null;
  let timer = null;
  let idleHandle = null;
  let usedIdleCallback = false;
  let done = false;

  const cleanup = () => {
    if (done) return;
    done = true;
    if (observer) observer.disconnect();
    if (timer) clearTimeout(timer);
    host.remove();
  };

  const run = () => {
    if (done) return;
    tags.forEach((tag) => host.appendChild(document.createElement(tag)));
    document.body.appendChild(host);

    // Drop the host as soon as the last element has rendered, rather than guessing a delay.
    // Stencil's `hydrated` class covers scoped components that render no children when empty
    // (an option-less `prism-select`, a content-less `prism-popover`).
    const allRendered = () =>
      tags.every((tag) => {
        const el = host.querySelector(tag);
        return (
          el &&
          (el.classList.contains("hydrated") ||
            el.childElementCount > 0 ||
            (el.shadowRoot && el.shadowRoot.childElementCount > 0))
        );
      });

    if (allRendered()) {
      cleanup();
      return;
    }

    observer = new MutationObserver(() => {
      if (allRendered()) cleanup();
    });
    observer.observe(host, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class"],
    });

    // Backstop: a chunk that never arrives must not leave the host in the DOM.
    timer = setTimeout(cleanup, 15000);
  };

  if (typeof window.requestIdleCallback === "function") {
    usedIdleCallback = true;
    idleHandle = window.requestIdleCallback(run, { timeout: 2000 });
  } else {
    idleHandle = setTimeout(run, 200);
  }

  return () => {
    if (usedIdleCallback && typeof window.cancelIdleCallback === "function") {
      window.cancelIdleCallback(idleHandle);
    } else if (!usedIdleCallback) {
      clearTimeout(idleHandle);
    }
    cleanup();
  };
}
